import { useQuery } from '@tanstack/react-query';

import {
    DEFAULT_CATALOG_SKU_EVENTS_LIMIT,
    getCatalogItemModificationEvents,
    normalizeExternalSku,
} from 'api/warehouse/catalogSkuEvents';
import { ApiError } from 'shared/errors/ApiError';

export const CATALOG_SKU_EVENTS_QUERY_KEY = 'catalogSkuEvents';

export const catalogSkuEventsQueryKeys = {
    all: [CATALOG_SKU_EVENTS_QUERY_KEY],
    detail: (externalSku, limit) => [CATALOG_SKU_EVENTS_QUERY_KEY, externalSku, limit],
};

/**
 * @param {Array<{createdAt?: string}>} events
 * @returns {Array<{createdAt?: string}>}
 */
export function sortEventsByCreatedAtDesc(events) {
    if (!Array.isArray(events)) {
        return [];
    }
    return [...events].sort((a, b) => {
        const aTime = a?.createdAt ? Date.parse(a.createdAt) : 0;
        const bTime = b?.createdAt ? Date.parse(b.createdAt) : 0;
        return bTime - aTime;
    });
}

/**
 * Fetches item modification events for a selected external SKU.
 *
 * @param {Object} options
 * @param {number|string|null|undefined} options.externalSku
 * @param {number} [options.limit=100]
 * @param {boolean} [options.enabled=true]
 */
export function useCatalogSkuEventsQuery({
    enabled = true,
    externalSku,
    limit = DEFAULT_CATALOG_SKU_EVENTS_LIMIT,
} = {}) {
    const normalizedExternalSku = normalizeExternalSku(externalSku);

    return useQuery({
        enabled: Boolean(enabled && normalizedExternalSku),
        queryKey: catalogSkuEventsQueryKeys.detail(normalizedExternalSku, limit),
        retry: false,
        queryFn: async ({ signal }) => {
            const response = await getCatalogItemModificationEvents(normalizedExternalSku, { limit, signal });
            if (!response?.status) {
                throw new ApiError(
                    response?.msg ?? 'Failed to fetch item modification events',
                    response?.statusCode,
                    response?.data,
                );
            }

            return {
                externalSku: response.data?.externalSku ?? normalizedExternalSku,
                name: response.data?.name ?? '',
                itemMasterEvents: sortEventsByCreatedAtDesc(response.data?.itemMasterEvents),
                itemInformationEvents: sortEventsByCreatedAtDesc(response.data?.itemInformationEvents),
            };
        },
    });
}
